import { arrayOf, element, func, node, oneOf, oneOfType, shape, string } from 'prop-types';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import constants from '../utils/constants';

import AlertBox from '../components/AlertBox';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import { Pagination, Stack } from '@mui/material';
import LabelAndDisplay from '../components/LabelAndDisplay';
import Authenticated from './Authenticated';
import SearchDialog from '../components/SearchDialog';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Loader from '../components/Loader';

const ListPage = ({
    children,
    title,
    summaries = [],
    description,
    instructionMessage,
    breadcrumbsLinks = [],
    searchOnly,
    defaultPayload,
    defaultResponseHandler,
    language = {
        code: 'en-US',
        name: 'English',
    },
    setLanguage = () => {},
    pageNumber = 1,
    setPageNumber = () => {},
    numberOfPages,
    loading = false,
}) => {
    const handleChange = (event, value) => {
        setPageNumber(value);
    };

    return (
        <Authenticated
            language={language}
            setLanguage={setLanguage}
        >
            <Box
                sx={{
                    flexGrow: 1
                }}
            >
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                >
                    <Typography
                        className='form-title'
                        variant="h2"
                    >
                        {title}
                    </Typography>

                    {
                        searchOnly &&
                        <Box>
                            <SearchDialog
                                searchOnly={searchOnly}
                                payload={defaultPayload}
                                responseHandler={defaultResponseHandler}
                            />
                        </Box>
                    }
                </Stack>

                <Breadcrumbs
                    home
                    links={breadcrumbsLinks}
                />

                <Box
                    sx={{
                        my: 2,
                        display: {
                            xs: 'none',
                            md: 'block',
                        },
                    }}
                >
                    <LanguageSwitcher
                        key={language.code}
                        prefix={'View in '}
                        selectedLanguageProp={language}
                        setSelectedLanguage={setLanguage}
                    />
                </Box>

                {
                    !!instructionMessage && 
                    <AlertBox
                        message={instructionMessage}
                    />
                }

                {
                    !!description &&
                    <Typography
                        sx={{
                            my: 2,
                            color: 'rgba(0, 0, 0, 0.74)',
                            textAlign: 'justify',
                        }}
                    >
                        {description}
                    </Typography>
                }

                <Box
                    sx={{ flexGrow: 1, my: 1 }}
                >
                    {
                        summaries.map(field => (
                            <LabelAndDisplay
                                key={field.label}
                                label={field.label}
                                value={field.value}
                            >
                                {field.children}
                            </LabelAndDisplay>
                        ))
                    }
                </Box>

                { children }
                {
                    loading &&
                    <Loader />
                }

                {
                    !!numberOfPages && !loading && numberOfPages > 1 &&
                    <Pagination
                        variant="outlined"
                        shape="rounded"
                        sx={{
                            mt: 5,
                            display: 'flex',
                        }}
                        count={numberOfPages}
                        page={pageNumber}
                        onChange={handleChange}
                    />
                }
            </Box>
        </Authenticated>
    )
}

ListPage.propTypes = {
    children: node.isRequired,
    title: string.isRequired,
    description: string,
    instructionMessage: string,
    searchOnly: arrayOf(
        oneOf(constants.searchOnlyEnums)
    ),
    searchStorageKey: string,
    handleSearchComplete: func,
    breadcrumbsLinks: arrayOf(
        shape({
            url: string,
            content: oneOfType([string, element]).isRequired,
        })
    ),
}

export default ListPage